import { useLocalStorage } from '@rehooks/local-storage';
import { Tabs } from 'antd';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { GameHUD, BattleHUD, BattleLobby } from '../components/GameHUD';
import { PanoramaNew } from '../components/PanoramaNew';
import RoundResultModal from '../components/RoundResultModal';
import { checkApiKeyValid } from '../config/mapyApi';
import { TOTAL_ROUNDS_MAX, MAX_STEPS_PER_ROUND } from '../constants/game';
import { emitApiLimitExceeded } from '../context/ApiLimitContext';
import gameModes from '../enums/modes';
import useGetRandomUserToken from '../hooks/useGetRandomUserToken';
import usePanoramaPreload from '../hooks/usePanoramaPreload';
import { addPanoramaPlaceToCurrentGameRounds, setTotalRoundScore } from '../redux/actions/game';
import {
    findUserFromBattleByRandomTokenId,
    generatePlaceInRadius,
    generateRandomRadius,
    getRandomCzechPlace,
    getRandomPlaceInRegion,
} from '../util';
import { clearMapCache } from '../util/map';
import { panoramaCache } from '../util/panoramaCache';
import { GuessingMapContainer } from './GuessingMapContainer';

const { TabPane } = Tabs;

export const GameScreen = ({ mode, radius, city, isGameStarted = true, isBattle, noMove = false }) => {
    const dispatch = useDispatch();
    const [smapDimensionLocalStorage] = useLocalStorage('smapDimension');
    const randomUserToken = useGetRandomUserToken();
    const currentGame = useSelector(state => state.game.currentGame);
    const lastPanoramaPlaceShown = useSelector(state => state.pano);
    const currentBattleInfo = useSelector(state => state.battle.currentBattle);

    const [mapDimension, setMapDimension] = useState(smapDimensionLocalStorage || 'normal');
    const [roundScore, setRoundScore] = useState(0);
    const [activeTabKey, setActiveTabKey] = useState('1');
    const [currentRoundISee, setCurrentRoundISee] = useState();
    const [roundGuessedDistance, setRoundGuessedDistance] = useState(null);
    const [guessedRandomPlace, setGuessedRandomPlace] = useState(null);
    const [allGuessedPoints, setAllGuessedPoints] = useState([]);
    const [currentRoundGuessedPoint, setCurrentRoundGuessedPoint] = useState();
    const [currentCity, setCurrentCity] = useState(null);
    const [resultModalVisible, setResultModalVisible] = useState(false);
    const [originalPanoramaPlace, setOriginalPanoramaPlace] = useState(null);
    const [bestPanoramaPlace, setBestPanoramaPlace] = useState(null); // best panorama chosen by mapy.cz API (according to original panorama place)
    const [panoramaLoading, setPanoramaLoading] = useState(true);
    const [nextRoundButtonVisible, setNextRoundButtonVisible] = useState(false);

    const isTabletOrMobileDevice = useMediaQuery({
        query: '(max-device-width: 1224px)',
    });

    const { totalScore, round, stepCount } = currentGame;
    const { round: currentRoundNumber, rounds: currentBattleRounds, myTotalScore, players } = currentBattleInfo;

    /**
     * OPTIMALIZACE: Pre-loading panoramat pro battle mód
     * V battle módu jsou všechna kola známa předem, takže můžeme
     * panoramata načíst do cache asynchronně na pozadí
     */
    usePanoramaPreload(currentBattleRounds, isGameStarted, isBattle);

    /**
     * OPTIMALIZACE: Cleanup cache při opuštění hry (unmount)
     * - Uvolní paměť použitou pro cachované mapy a panoramata
     * - Resetuje session pro panoramata
     */
    useEffect(() => {
        return () => {
            // Cleanup při opuštění GameScreen
            clearMapCache();
            panoramaCache.clearSession();
        };
    }, []);

    /**
     * Kontrola platnosti API klíče při startu hry
     * Pokud je vyčerpán limit, zobrazí se modal s informací
     */
    useEffect(() => {
        const verifyApiKey = async () => {
            const isValid = await checkApiKeyValid();
            if (!isValid) {
                emitApiLimitExceeded();
            }
        };
        verifyApiKey();
    }, []);

    // FIXME: to load whole map layer when the map is minimized before
    useEffect(() => {
        window.dispatchEvent(new Event('resize'));
    }, [activeTabKey]);

    useEffect(() => {
        // Do not do this for mobile - it breaks map rendering in next rounds
        if (!isMobile) {
            let roundNumber;
            if (isBattle) {
                roundNumber = currentRoundNumber;
            } else {
                roundNumber = round;
            }
            if (!roundNumber || roundNumber === 1) {
                setMapDimension('normal');
            } else {
                setMapDimension('min');
            }
        }
    }, [isBattle, currentRoundNumber, round]);


    // Executed when the game is started or a page is re/loaded and rounds are already generated
    useEffect(() => {
        if (isBattle) {
            if (isGameStarted && currentBattleRounds.length) {
                const currentRound = currentBattleRounds[currentRoundNumber - 1];
                const { city: cityToGuess, panoramaPlace: panoramaPlaceToGuess } = currentRound;
                if (lastPanoramaPlaceShown && lastPanoramaPlaceShown.lat && lastPanoramaPlaceShown.lon) {
                    setOriginalPanoramaPlace({
                        latitude: lastPanoramaPlaceShown.lat,
                        longitude: lastPanoramaPlaceShown.lon,
                    });
                } else {
                    setOriginalPanoramaPlace(panoramaPlaceToGuess);
                }
                setCurrentCity(cityToGuess);
                // set the current round user can see to prevent panorama rerendering after the first guess is made by somebody
                setCurrentRoundISee(currentRound.roundId);
                // load guessed points to be persistent
                const myUser = findUserFromBattleByRandomTokenId(players, randomUserToken);
                setCurrentRoundGuessedPoint(myUser[`round${currentRoundNumber}`] ?? null);
            }
        } else {
            findNewPanorama();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGameStarted]);

    useEffect(() => {
        // run after the currentRoundISee is set before (when the game is started or a page is reloaded)
        if (currentBattleRounds.length && isGameStarted && currentRoundISee) {
            const currentRound = currentBattleRounds[currentRoundNumber - 1];
            const { city: cityToGuess, panoramaPlace: panoramaPlaceToGuess } = currentRound;

            // check if a new round was started
            if (currentRoundISee < currentRound.roundId) {
                handleClearBestPanoramaPlace();
                setCurrentRoundISee(currentRound.roundId);
                setOriginalPanoramaPlace(panoramaPlaceToGuess);
            }

            setCurrentCity(cityToGuess);
            // load guessed points to be persistent
            const myUser = findUserFromBattleByRandomTokenId(players, randomUserToken);
            setCurrentRoundGuessedPoint(myUser[`round${currentRoundNumber}`] ?? null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGameStarted, currentBattleRounds, currentRoundNumber, players]);

    const changeNextRoundButtonVisibility = isVisible => {
        setNextRoundButtonVisible(isVisible);
    };

    const findNewPanorama = () => {
        handleClearBestPanoramaPlace();
        if (mode === gameModes.random) {
            radius = generateRandomRadius();
            city = getRandomCzechPlace();
        }
        if (mode === gameModes.randomRegion) {
            const { regionNutCode } = currentGame;
            radius = generateRandomRadius();
            city = getRandomPlaceInRegion(regionNutCode);
        }
        setCurrentCity(city);
        const generatedPanoramaPlace = generatePlaceInRadius(radius, city);
        setOriginalPanoramaPlace(generatedPanoramaPlace);
        // mobile
        setActiveTabKey('1');
    };

    /**
     * OPTIMALIZACE: Reset panorama cache pri startu nove hry
     * Zajistuje, ze se v nove hre nebudou opakovat panoramata z predchozi
     */
    useEffect(() => {
        if (!isBattle && round === 1) {
            panoramaCache.clearSession();
        }
    }, [isBattle, round]);

    /**
     * Triggers re-render to find a new panorama
     */
    const handleClearBestPanoramaPlace = () => {
        setBestPanoramaPlace(null);
    };

    const handleSetBestPanoramaPlace = bestPanoramaPlace => {
        // Podpora starého i nového formátu
        let lat, lon;
        if (bestPanoramaPlace._data?.mark) {
            // Starý formát (SMap.Pano)
            const data = bestPanoramaPlace._data.mark;
            lat = data.lat;
            lon = data.lon;
        } else {
            // Nový formát (JS Panorama API)
            lat = bestPanoramaPlace.lat;
            lon = bestPanoramaPlace.lon;
        }

        dispatch(
            addPanoramaPlaceToCurrentGameRounds({
                roundId: round,
                lat,
                lon,
                bestPanoramaPlace,
            }),
        );
        setOriginalPanoramaPlace({
            latitude: lat,
            longitude: lon,
        });
        setBestPanoramaPlace(bestPanoramaPlace);
    };

    const changePanoramaLoadingState = loading => {
        setPanoramaLoading(loading);
    };

    const saveRoundResult = (score, distance) => {
        setRoundScore(Math.round(score));
        if (isBattle) {
            dispatch(setTotalRoundScore(Math.round(myTotalScore + score)));
        } else {
            dispatch(setTotalRoundScore(Math.round(totalScore + score)));
        }
        setRoundGuessedDistance(distance);
    };

    const evaluateGuessedRound = guessedPointsInRound => {
        if (mode === gameModes.random || mode === gameModes.randomRegion) {
            const { obec, okres, kraj, coatOfArmsDescription, coatOfArmsFlagDescription } = currentCity;
            setGuessedRandomPlace({ obec, okres, kraj, coatOfArmsDescription, coatOfArmsFlagDescription });
        }
        setAllGuessedPoints([...allGuessedPoints, guessedPointsInRound]);
        setCurrentRoundGuessedPoint(guessedPointsInRound);
        setResultModalVisible(true);
    };

    return (
        <>
            {isTabletOrMobileDevice ? (
                <Tabs
                    defaultActiveKey="1"
                    centered
                    activeKey={activeTabKey}
                    onChange={activeKey => setActiveTabKey(activeKey)}
                >
                    <TabPane tab="panorama" key="1">
                        <PanoramaNew
                            originalPanoramaPlace={originalPanoramaPlace}
                            bestPanoramaPlace={bestPanoramaPlace}
                            panoramaLoading={panoramaLoading}
                            changePanoramaLoadingState={changePanoramaLoadingState}
                            isGameStarted={isGameStarted}
                            isBattle={isBattle}
                            noMove={noMove}
                            onSetBestPanoramaPlace={handleSetBestPanoramaPlace}
                            isVisible={activeTabKey === '1'}
                        />
                    </TabPane>
                    <TabPane tab="mapa" key="2">
                        <GuessingMapContainer
                            isBattle={isBattle}
                            visible={isBattle ? isGameStarted : true}
                            evaluateGuessedRound={evaluateGuessedRound}
                            currentRoundGuessedPoint={currentRoundGuessedPoint}
                            panoramaLoading={panoramaLoading}
                            findNewPanorama={findNewPanorama}
                            saveRoundResult={saveRoundResult}
                            bestPanoramaPlace={bestPanoramaPlace}
                            allGuessedPoints={allGuessedPoints}
                            isGameStarted={isGameStarted}
                            currentCity={currentCity}
                            nextRoundButtonVisible={nextRoundButtonVisible}
                            changeNextRoundButtonVisibility={changeNextRoundButtonVisibility}
                            mapDimension={mapDimension}
                        />
                    </TabPane>
                </Tabs>
            ) : (
                <>
                    <div className="game-screen-container">
                        {/* Battle lobby - before game starts */}
                        {isBattle && !isGameStarted && <BattleLobby />}
                        <PanoramaNew
                            originalPanoramaPlace={originalPanoramaPlace}
                            bestPanoramaPlace={bestPanoramaPlace}
                            panoramaLoading={panoramaLoading}
                            changePanoramaLoadingState={changePanoramaLoadingState}
                            isGameStarted={isGameStarted}
                            isBattle={isBattle}
                            noMove={noMove}
                            onSetBestPanoramaPlace={handleSetBestPanoramaPlace}
                        />
                        {/* GeoGuessr-style HUD overlay - during game */}
                        {isBattle && isGameStarted ? (
                            <BattleHUD
                                round={currentRoundNumber}
                                totalRounds={TOTAL_ROUNDS_MAX}
                                players={players}
                                myTotalScore={myTotalScore}
                                isRoundActive={isGameStarted && !nextRoundButtonVisible}
                                myPlayerId={randomUserToken}
                            />
                        ) : !isBattle ? (
                            <GameHUD
                                round={round}
                                totalRounds={TOTAL_ROUNDS_MAX}
                                totalScore={totalScore}
                                isLoading={panoramaLoading}
                                stepCount={stepCount || 0}
                                maxSteps={MAX_STEPS_PER_ROUND}
                            />
                        ) : null}
                    </div>
                    <GuessingMapContainer
                        isBattle={isBattle}
                        visible={isBattle ? isGameStarted : true}
                        evaluateGuessedRound={evaluateGuessedRound}
                        currentRoundGuessedPoint={currentRoundGuessedPoint}
                        panoramaLoading={panoramaLoading}
                        findNewPanorama={findNewPanorama}
                        saveRoundResult={saveRoundResult}
                        bestPanoramaPlace={bestPanoramaPlace}
                        allGuessedPoints={allGuessedPoints}
                        isGameStarted={isGameStarted}
                        currentCity={currentCity}
                        nextRoundButtonVisible={nextRoundButtonVisible}
                        changeNextRoundButtonVisibility={changeNextRoundButtonVisibility}
                        mapDimension={mapDimension}
                        onSetMapDimension={dimension => setMapDimension(dimension)}
                    />
                </>
            )}
            <RoundResultModal
                visible={resultModalVisible}
                closeModal={() => setResultModalVisible(false)}
                roundGuessedDistance={roundGuessedDistance}
                roundScore={roundScore}
                totalRoundScore={totalScore}
                isBattle={isBattle}
                guessedRandomPlace={guessedRandomPlace}
            />
        </>
    );
};
